import React, { useCallback, useEffect, useState } from "react";
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Table } from "antd";
import CcButton from "../../CoilConfigurator/components/CcButton";
import ConfirmDialog from "../../CoilConfigurator/components/ConfirmDialog";
import BlastFrameCatalogueAddModal, {
  type BlastFrameCatalogueAddKind,
  type BlastFrameCatalogueAddPayload,
} from "../components/BlastFrameCatalogueAddModal";
import { configurationService } from "../services/configurationService";
import type {
  BlastFrameCatalogueCarriageRow,
  BlastFrameCatalogueFrameSize,
  BlastFrameCatalogueKite,
  BlastFrameCatalogueVersion,
  InductionCoilCatalogueFrame,
  InductionCoilCatalogueVersion,
  OnPipeCatalogue,
} from "../models/types";

const formatMm = (value: number | undefined) =>
  value == null || Number.isNaN(value) ? "—" : `${value} mm`;

const inductionCoilFrameId = (row: InductionCoilCatalogueFrame) =>
  `${row.nbIn}|${row.clearanceType}|${row.index}`;

const blastFrameKiteId = (row: BlastFrameCatalogueKite) => `${row.id}|${row.heads}`;

type DeleteTarget =
  | { kind: "bf-frame-size"; id: string; label: string }
  | { kind: "bf-version"; id: string; label: string }
  | { kind: "bf-kite"; id: string; label: string }
  | { kind: "bf-carriage-row"; id: string; label: string }
  | { kind: "ic-frame"; id: string; label: string }
  | { kind: "ic-version"; id: string; label: string };

const CataloguePanel = () => {
  const [catalogue, setCatalogue] = useState<OnPipeCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [equipmentTab, setEquipmentTab] = useState<"blast-frame" | "induction-coil">("blast-frame");
  const [addKind, setAddKind] = useState<BlastFrameCatalogueAddKind | null>(null);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addFeedback, setAddFeedback] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadCatalogue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await configurationService.getCatalogue();
      setCatalogue(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load catalogue.");
      setCatalogue(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCatalogue();
  }, [loadCatalogue]);

  const openAdd = (kind: BlastFrameCatalogueAddKind) => {
    setAddError(null);
    setAddFeedback(null);
    setAddKind(kind);
  };

  const closeAdd = () => {
    if (addSaving) return;
    setAddKind(null);
    setAddError(null);
  };

  const saveAdd = async (payload: BlastFrameCatalogueAddPayload) => {
    setAddSaving(true);
    setAddError(null);
    try {
      if (payload.kind === "frame-size") {
        await configurationService.createBlastFrameFrameSize(payload.item);
      } else if (payload.kind === "version") {
        await configurationService.createBlastFrameVersion(payload.item);
      } else if (payload.kind === "kite") {
        await configurationService.createBlastFrameKite(payload.item);
      } else if (payload.kind === "carriage-row") {
        await configurationService.createBlastFrameCarriageRow(payload.item);
      } else if (payload.kind === "ic-frame") {
        await configurationService.createInductionCoilFrame(payload.item);
      } else {
        await configurationService.createInductionCoilVersion(payload.item);
      }
      setAddKind(null);
      setAddFeedback("Catalogue item added.");
      await loadCatalogue();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "We couldn't add this catalogue item.");
    } finally {
      setAddSaving(false);
    }
  };

  const requestDeleteFrame = (row: InductionCoilCatalogueFrame) => {
    setDeleteError(null);
    setAddFeedback(null);
    setDeleteTarget({
      kind: "ic-frame",
      id: inductionCoilFrameId(row),
      label: `${row.nbIn}" ${row.clearanceType} (index ${row.index})`,
    });
  };

  const requestDeleteVersion = (row: InductionCoilCatalogueVersion) => {
    setDeleteError(null);
    setAddFeedback(null);
    setDeleteTarget({
      kind: "ic-version",
      id: row.id,
      label: row.id,
    });
  };

  const requestDeleteBlastFrameSize = (row: BlastFrameCatalogueFrameSize) => {
    setDeleteError(null);
    setAddFeedback(null);
    setDeleteTarget({
      kind: "bf-frame-size",
      id: row.label,
      label: row.label,
    });
  };

  const requestDeleteBlastVersion = (row: BlastFrameCatalogueVersion) => {
    setDeleteError(null);
    setAddFeedback(null);
    setDeleteTarget({
      kind: "bf-version",
      id: row.id,
      label: row.id,
    });
  };

  const requestDeleteBlastKite = (row: BlastFrameCatalogueKite) => {
    setDeleteError(null);
    setAddFeedback(null);
    setDeleteTarget({
      kind: "bf-kite",
      id: blastFrameKiteId(row),
      label: `${row.id} / ${row.heads}`,
    });
  };

  const requestDeleteBlastCarriageRow = (row: BlastFrameCatalogueCarriageRow) => {
    setDeleteError(null);
    setAddFeedback(null);
    setDeleteTarget({
      kind: "bf-carriage-row",
      id: row.frameSize,
      label: row.frameSize,
    });
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      if (deleteTarget.kind === "bf-frame-size") {
        await configurationService.deleteBlastFrameFrameSize(deleteTarget.id);
      } else if (deleteTarget.kind === "bf-version") {
        await configurationService.deleteBlastFrameVersion(deleteTarget.id);
      } else if (deleteTarget.kind === "bf-kite") {
        await configurationService.deleteBlastFrameKite(deleteTarget.id);
      } else if (deleteTarget.kind === "bf-carriage-row") {
        await configurationService.deleteBlastFrameCarriageRow(deleteTarget.id);
      } else if (deleteTarget.kind === "ic-frame") {
        await configurationService.deleteInductionCoilFrame(deleteTarget.id);
      } else {
        await configurationService.deleteInductionCoilVersion(deleteTarget.id);
      }
      setDeleteTarget(null);
      setAddFeedback("Catalogue item deleted.");
      await loadCatalogue();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "We couldn't delete this catalogue item.",
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading && !catalogue) {
    return (
      <section className="op-form-card">
        <p className="op-muted">Loading catalogue…</p>
      </section>
    );
  }

  if ((error && !catalogue) || !catalogue) {
    return (
      <section className="op-form-card">
        <p className="op-field-error" role="alert">
          {error || "Catalogue is unavailable."}
        </p>
        <CcButton variant="secondary" onClick={() => void loadCatalogue()}>
          Retry
        </CcButton>
      </section>
    );
  }

  const { blastFrame, inductionCoil } = catalogue;

  return (
    <div className="op-catalogue-panel">
      <div className="op-info-note">
        <InfoCircleOutlined aria-hidden />
        <p>
          Master catalogue data used by Blast Frame and Induction Coil selection rules. Use Add to
          create new catalogue rows for either equipment type.
        </p>
      </div>

      {addFeedback ? (
        <p className="op-feedback" role="status">
          {addFeedback}
        </p>
      ) : null}

      <div className="op-catalogue-panel__header">
        <div className="op-catalogue-panel__tabs" role="tablist" aria-label="Catalogue equipment">
          <button
            type="button"
            className={`op-catalogue-panel__tab${
              equipmentTab === "blast-frame" ? " op-catalogue-panel__tab--active" : ""
            }`}
            role="tab"
            aria-selected={equipmentTab === "blast-frame"}
            onClick={() => setEquipmentTab("blast-frame")}
          >
            Blast Frame ({blastFrame.ruleVersion})
          </button>
          <button
            type="button"
            className={`op-catalogue-panel__tab${
              equipmentTab === "induction-coil" ? " op-catalogue-panel__tab--active" : ""
            }`}
            role="tab"
            aria-selected={equipmentTab === "induction-coil"}
            onClick={() => setEquipmentTab("induction-coil")}
          >
            Induction Coil ({inductionCoil.ruleVersion})
          </button>
        </div>
      </div>

      {equipmentTab === "blast-frame" ? (
        <>
          <section className="op-cat-block">
            <h3>Rule constants</h3>
            <p className="op-cat-block__desc">
              Fixed clearances and seal geometry used by Blast Frame sizing rules.
            </p>
            <div className="op-form-card">
              <div className="op-input-group__grid">
                <div className="op-input-stat">
                  <span className="op-input-stat__label">Small head max steel OD</span>
                  <span className="op-input-stat__value">
                    {formatMm(blastFrame.smallHeadMaxSteelOdMm)}
                  </span>
                </div>
                <div className="op-input-stat">
                  <span className="op-input-stat__label">Chamfer side clearance</span>
                  <span className="op-input-stat__value">
                    {formatMm(blastFrame.chamferSideClearanceMm)}
                  </span>
                </div>
                <div className="op-input-stat">
                  <span className="op-input-stat__label">CWC overall length margin</span>
                  <span className="op-input-stat__value">
                    {formatMm(blastFrame.cwcOverallLengthMarginMm)}
                  </span>
                </div>
                <div className="op-input-stat">
                  <span className="op-input-stat__label">Carriage coating inner offset</span>
                  <span className="op-input-stat__value">
                    {formatMm(blastFrame.carriageCoatingInnerOffsetMm)}
                  </span>
                </div>
                <div className="op-input-stat">
                  <span className="op-input-stat__label">Carriage coating outer offset</span>
                  <span className="op-input-stat__value">
                    {formatMm(blastFrame.carriageCoatingOuterOffsetMm)}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="op-cat-block">
            <div className="op-cat-block__head">
              <div>
                <h3>Frame sizes</h3>
                <p className="op-cat-block__desc">
                  Wheel OD range labels indexed for diameter category selection.
                </p>
              </div>
              <CcButton variant="primary" icon={<PlusOutlined />} onClick={() => openAdd("frame-size")}>
                Add Frame Size
              </CcButton>
            </div>
            <div className="op-form-card op-form-card--flush">
              <Table
                className="op-table"
                rowKey="label"
                pagination={false}
                dataSource={blastFrame.frameSizes}
                columns={[
                  { title: "Label", dataIndex: "label", key: "label" },
                  { title: "Index", dataIndex: "index", key: "index" },
                  {
                    title: "Min",
                    key: "minMm",
                    render: (_: unknown, row) => formatMm(row.minMm),
                  },
                  {
                    title: "Max",
                    key: "maxMm",
                    render: (_: unknown, row) => formatMm(row.maxMm),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 72,
                    render: (_: unknown, row: BlastFrameCatalogueFrameSize) => (
                      <CcButton
                        variant="icon"
                        danger
                        aria-label={`Delete frame size ${row.label}`}
                        onClick={() => requestDeleteBlastFrameSize(row)}
                      >
                        <DeleteOutlined />
                      </CcButton>
                    ),
                  },
                ]}
              />
            </div>
          </section>

          <section className="op-cat-block">
            <div className="op-cat-block__head">
              <div>
                <h3>Length versions</h3>
                <p className="op-cat-block__desc">Inner-wheel, overall, and carriage lengths by version.</p>
              </div>
              <CcButton variant="primary" icon={<PlusOutlined />} onClick={() => openAdd("version")}>
                Add Version
              </CcButton>
            </div>
            <div className="op-form-card op-form-card--flush">
              <Table
                className="op-table"
                rowKey="id"
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                dataSource={blastFrame.versions}
                columns={[
                  { title: "Version", dataIndex: "id", key: "id" },
                  { title: "Index", dataIndex: "index", key: "index" },
                  {
                    title: "Inner wheel length",
                    key: "innerWheelLengthMm",
                    render: (_: unknown, row) => formatMm(row.innerWheelLengthMm),
                  },
                  {
                    title: "Overall length",
                    key: "overallLengthMm",
                    render: (_: unknown, row) => formatMm(row.overallLengthMm),
                  },
                  {
                    title: "Carriage length",
                    key: "carriageLengthMm",
                    render: (_: unknown, row) => formatMm(row.carriageLengthMm),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 72,
                    render: (_: unknown, row: BlastFrameCatalogueVersion) => (
                      <CcButton
                        variant="icon"
                        danger
                        aria-label={`Delete version ${row.id}`}
                        onClick={() => requestDeleteBlastVersion(row)}
                      >
                        <DeleteOutlined />
                      </CcButton>
                    ),
                  },
                ]}
              />
            </div>
          </section>

          <section className="op-cat-block">
            <div className="op-cat-block__head">
              <div>
                <h3>Blast Carriage / Kite Types</h3>
                <p className="op-cat-block__desc">
                  Carriage/kite options with head size, OD offsets, bracket clearance, and seal bore
                  data.
                </p>
              </div>
              <CcButton variant="primary" icon={<PlusOutlined />} onClick={() => openAdd("kite")}>
                Add Kite
              </CcButton>
            </div>
            <div className="op-form-card op-form-card--flush">
              <Table
                className="op-table"
                rowKey={(row) => blastFrameKiteId(row)}
                pagination={false}
                dataSource={blastFrame.kites}
                columns={[
                  { title: "Kite type", dataIndex: "id", key: "id" },
                  { title: "Heads", dataIndex: "heads", key: "heads" },
                  {
                    title: "2-tie blast",
                    key: "requiresTwoTieBarBlast",
                    render: (_: unknown, row) => (row.requiresTwoTieBarBlast ? "Yes" : "No"),
                  },
                  {
                    title: "4-tie blast",
                    key: "requiresFourTieBarBlast",
                    render: (_: unknown, row) => (row.requiresFourTieBarBlast ? "Yes" : "No"),
                  },
                  {
                    title: "Clearance margin",
                    key: "clearanceMarginMm",
                    render: (_: unknown, row) => formatMm(row.clearanceMarginMm),
                  },
                  {
                    title: "Clearance offsets",
                    key: "clearanceOffsetsMm",
                    render: (_: unknown, row) =>
                      row.clearanceOffsetsMm.map((v) => `${v} mm`).join(", "),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 72,
                    render: (_: unknown, row: BlastFrameCatalogueKite) => (
                      <CcButton
                        variant="icon"
                        danger
                        aria-label={`Delete kite ${blastFrameKiteId(row)}`}
                        onClick={() => requestDeleteBlastKite(row)}
                      >
                        <DeleteOutlined />
                      </CcButton>
                    ),
                  },
                ]}
              />
            </div>
          </section>

          <section className="op-cat-block">
            <div className="op-cat-block__head">
              <div>
                <h3>Carriage rows</h3>
                <p className="op-cat-block__desc">
                  Outer blast carriage hole PCD by frame size.
                </p>
              </div>
              <CcButton
                variant="primary"
                icon={<PlusOutlined />}
                onClick={() => openAdd("carriage-row")}
              >
                Add Carriage Row
              </CcButton>
            </div>
            <div className="op-form-card op-form-card--flush">
              <Table
                className="op-table"
                rowKey="frameSize"
                pagination={false}
                locale={{ emptyText: "No carriage rows yet." }}
                dataSource={blastFrame.carriageRows || []}
                columns={[
                  { title: "Frame size", dataIndex: "frameSize", key: "frameSize" },
                  {
                    title: "Outer blast carriage hole PCD",
                    key: "outerBlastCarriageHolePcdMm",
                    render: (_: unknown, row) => formatMm(row.outerBlastCarriageHolePcdMm),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 72,
                    render: (_: unknown, row: BlastFrameCatalogueCarriageRow) => (
                      <CcButton
                        variant="icon"
                        danger
                        aria-label={`Delete carriage row ${row.frameSize}`}
                        onClick={() => requestDeleteBlastCarriageRow(row)}
                      >
                        <DeleteOutlined />
                      </CcButton>
                    ),
                  },
                ]}
              />
            </div>
          </section>
        </>
      ) : (
        <>
          <section className="op-cat-block">
            <h3>Rule constants</h3>
            <p className="op-cat-block__desc">Clearances and preferred versions for Induction Coil selection.</p>
            <div className="op-form-card">
              <div className="op-input-group__grid">
                <div className="op-input-stat">
                  <span className="op-input-stat__label">Coating clearance</span>
                  <span className="op-input-stat__value">
                    {formatMm(inductionCoil.coatingClearanceMm)}
                  </span>
                </div>
                <div className="op-input-stat">
                  <span className="op-input-stat__label">Preferred non-CWC version</span>
                  <span className="op-input-stat__value">
                    {inductionCoil.preferredNonCwcVersionId || "—"}
                  </span>
                </div>
                <div className="op-input-stat">
                  <span className="op-input-stat__label">Step-down coating thickness</span>
                  <span className="op-input-stat__value">
                    {formatMm(inductionCoil.stepDownCoatingThicknessMm)}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="op-cat-block">
            <div className="op-cat-block__head">
              <div>
                <h3>Frames</h3>
                <p className="op-cat-block__desc">
                  Nominal bore frames with clearance type and inside diameter.
                </p>
              </div>
              <CcButton variant="primary" icon={<PlusOutlined />} onClick={() => openAdd("ic-frame")}>
                Add Frame
              </CcButton>
            </div>
            <div className="op-form-card op-form-card--flush">
              <Table
                className="op-table"
                rowKey={(row) => inductionCoilFrameId(row)}
                pagination={{ pageSize: 12, hideOnSinglePage: true }}
                dataSource={inductionCoil.frames}
                columns={[
                  { title: "NB (in)", dataIndex: "nbIn", key: "nbIn" },
                  { title: "Index", dataIndex: "index", key: "index" },
                  { title: "Clearance type", dataIndex: "clearanceType", key: "clearanceType" },
                  {
                    title: "ID",
                    key: "idMm",
                    render: (_: unknown, row) => formatMm(row.idMm),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 72,
                    render: (_: unknown, row: InductionCoilCatalogueFrame) => (
                      <CcButton
                        variant="icon"
                        danger
                        aria-label={`Delete frame ${inductionCoilFrameId(row)}`}
                        onClick={() => requestDeleteFrame(row)}
                      >
                        <DeleteOutlined />
                      </CcButton>
                    ),
                  },
                ]}
              />
            </div>
          </section>

          <section className="op-cat-block">
            <div className="op-cat-block__head">
              <div>
                <h3>Bottom-end versions</h3>
                <p className="op-cat-block__desc">
                  Length stack-up for each induction coil bottom-end version.
                </p>
              </div>
              <CcButton variant="primary" icon={<PlusOutlined />} onClick={() => openAdd("ic-version")}>
                Add Version
              </CcButton>
            </div>
            <div className="op-form-card op-form-card--flush">
              <Table
                className="op-table"
                rowKey="id"
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                dataSource={inductionCoil.versions}
                columns={[
                  { title: "Version", dataIndex: "id", key: "id" },
                  { title: "Index", dataIndex: "index", key: "index" },
                  {
                    title: "Bottom end length",
                    key: "bottomEndLengthMm",
                    render: (_: unknown, row) => formatMm(row.bottomEndLengthMm),
                  },
                  {
                    title: "Inside wheels length",
                    key: "insideWheelsLengthMm",
                    render: (_: unknown, row) => formatMm(row.insideWheelsLengthMm),
                  },
                  {
                    title: "Heat length",
                    key: "heatLengthMm",
                    render: (_: unknown, row) => formatMm(row.heatLengthMm),
                  },
                  {
                    title: "Total length (swivel plate)",
                    key: "totalLengthSwivelPlateMm",
                    render: (_: unknown, row) => formatMm(row.totalLengthSwivelPlateMm),
                  },
                  {
                    title: "Total length (tufnol discs)",
                    key: "totalLengthTufnolDiscsMm",
                    render: (_: unknown, row) => formatMm(row.totalLengthTufnolDiscsMm),
                  },
                  {
                    title: "Inside tufnol discs",
                    key: "insideTufnolDiscsLengthMm",
                    render: (_: unknown, row) => formatMm(row.insideTufnolDiscsLengthMm),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 72,
                    render: (_: unknown, row: InductionCoilCatalogueVersion) => (
                      <CcButton
                        variant="icon"
                        danger
                        aria-label={`Delete version ${row.id}`}
                        onClick={() => requestDeleteVersion(row)}
                      >
                        <DeleteOutlined />
                      </CcButton>
                    ),
                  },
                ]}
              />
            </div>
          </section>
        </>
      )}

      <BlastFrameCatalogueAddModal
        open={Boolean(addKind)}
        kind={addKind}
        saving={addSaving}
        error={addError}
        onClose={closeAdd}
        onSave={saveAdd}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Catalogue Item"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.label}"? This cannot be undone.${
                deleteError ? ` ${deleteError}` : ""
              }`
            : ""
        }
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        loading={deleting}
        onClose={closeDelete}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default CataloguePanel;
